import { UNSAFE_PortalProvider } from '@react-aria/overlays';
import { RiCloseLine } from '@remixicon/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { createContext, ReactNode, useContext, useRef } from 'react';
import { Dialog, type DialogProps, Heading, Modal, ModalOverlay, type ModalOverlayProps } from 'react-aria-components';

import { AgButton } from './AgButton';
import { cn } from './utils';

const modalVariants = cva(
  [
    'relative w-full flex flex-col gap-6 max-h-[90vh]',
    'rounded-[16px] border border-neutral-5 bg-white p-6 shadow-lg outline-none',
    'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
    'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
  ],
  {
    variants: {
      size: { xs: 'max-w-[320px]', sm: 'max-w-[384px]', md: 'max-w-[512px]', lg: 'max-w-[672px]', xl: 'max-w-[896px]' },
    },
    defaultVariants: { size: 'md' },
  }
);

const AgModalContext = createContext<{ onClose: () => void } | null>(null);

interface AgModalSlotProps {
  /** Props passed to the ModalOverlay wrapper */
  overlay?: Omit<ModalOverlayProps, 'isOpen' | 'onOpenChange' | 'isDismissable' | 'children'>;
  /** Props passed to the inner Modal */
  modal?: Omit<ModalOverlayProps, 'children'>;
  /** Props passed to the Dialog */
  dialog?: Omit<DialogProps, 'children' | 'className'>;
}

export interface AgModalProps extends VariantProps<typeof modalVariants> {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close (escape, backdrop click, close button) */
  onClose: () => void;
  /** Modal title displayed in the header */
  title?: ReactNode;
  /** Hide the close button (default: false) */
  hideCloseButton?: boolean;
  /** Whether the modal can be dismissed by clicking the backdrop or pressing Escape (default: true) */
  isDismissable?: boolean;
  /** Modal content */
  children: ReactNode;
  /** Additional class names for the dialog panel */
  className?: string;
  /** Props forwarded to inner ModalOverlay, Modal, and Dialog components */
  slotProps?: AgModalSlotProps;
}

/** notistack renders every toast inside this container (see `.notistack-SnackbarContainer` in index.css). */
const isToast = (element: Element) => element.closest('.notistack-SnackbarContainer') !== null;

/**
 * Modal dialog built on react-aria-components.
 *
 * Use with `AgModalContent` and `AgModalActions` sub-components for standard layout:
 *
 * ```tsx
 * <AgModal open={open} onClose={onClose} title="Edit Item" size="md">
 *   <AgModalContent>
 *     {/* form fields, tables, etc. *\/}
 *   </AgModalContent>
 *   <AgModalActions>
 *     <AgButton variant="primary" type="submit">Save</AgButton>
 *   </AgModalActions>
 * </AgModal>
 * ```
 *
 * Sizes: `xs` (320px), `sm` (384px), `md` (512px, default), `lg` (672px), `xl` (896px)
 */
export function AgModal({
  open,
  onClose,
  title,
  hideCloseButton = false,
  isDismissable = true,
  children,
  className,
  size,
  slotProps,
}: AgModalProps) {
  // Portal container so react-aria overlays opened from within this modal render inside the modal's DOM subtree — placing them in the modal's stacking context (z-index 110000), above modal content but below the backdrop.
  const portalRef = useRef<HTMLDivElement>(null);

  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      isDismissable={isDismissable}
      {...slotProps?.overlay}
      // A toast shown over the modal (e.g. a refused save's reference code and Contact support) must stay
      // clickable without dismissing the modal underneath it.
      shouldCloseOnInteractOutside={(element) =>
        !isToast(element) && (slotProps?.overlay?.shouldCloseOnInteractOutside?.(element) ?? true)
      }
      style={{
        // Ag* dropdowns use z-index 200000, so the backdrop uses 210000 and always wins over page-level dropdowns. Inline style because bare non-standard z-index values may not compile under Tailwind's `--*: initial`.
        // Dropdowns opened from WITHIN a modal are portaled inside it via UNSAFE_PortalProvider, so they never need to beat this global z-index.
        ...(slotProps?.overlay?.style as React.CSSProperties),
        zIndex: 210000,
      }}
      className={cn(
        'fixed inset-0 flex items-center justify-center bg-black/50 entering:animate-in entering:fade-in-0 exiting:animate-out exiting:fade-out-0',
        slotProps?.overlay?.className
      )}
    >
      <Modal {...slotProps?.modal} className={cn(modalVariants({ size }), slotProps?.modal?.className, className)}>
        <Dialog
          {...slotProps?.dialog}
          className={cn('flex flex-col flex-1 min-h-0 outline-none', slotProps?.dialog?.className)}
        >
          <AgModalContext.Provider value={{ onClose }}>
            {title && (
              <Heading
                slot="title"
                className="pr-8 mb-5 text-18 font-medium leading-[26px] tracking-[-0.2px] text-neutral-text"
              >
                {title}
              </Heading>
            )}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 rounded-full bg-neutral-alpha-2 p-2 transition-colors hover:bg-neutral-alpha-3"
                aria-label="Close"
              >
                <RiCloseLine className="h-4 w-4" />
              </button>
            )}
            <UNSAFE_PortalProvider getContainer={() => portalRef.current!}>{children}</UNSAFE_PortalProvider>
            <div ref={portalRef} />
          </AgModalContext.Provider>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

AgModal.displayName = 'AgModal';

/* ─── Sub-components ─────────────────────────────────────────────── */

export interface AgModalContentProps {
  children: ReactNode;
  className?: string;
}

/** Scrollable body area for modal content. Use inside `AgModal`. */
export function AgModalContent({ children, className }: AgModalContentProps) {
  return <div className={cn('flex-1 overflow-y-auto', className)}>{children}</div>;
}
AgModalContent.displayName = 'AgModalContent';

export interface AgModalActionsProps {
  children: ReactNode;
  /** Override the default cancel handler (defaults to AgModal's onClose) */
  onCancel?: () => void;
  /** Override the default cancel button with a custom element. Pass `null` to hide. */
  cancel?: ReactNode | null;
  className?: string;
}

/**
 * Action bar for modal dialogs. Cancel button on the left, actions on the right.
 *
 * By default, renders a white "Cancel" button that calls the parent `AgModal`'s `onClose`.
 *
 * ```tsx
 * // Default cancel button — calls AgModal's onClose automatically
 * <AgModalActions>
 *   <AgButton variant="primary">Save</AgButton>
 * </AgModalActions>
 *
 * // Custom cancel handler
 * <AgModalActions onCancel={customHandler}>
 *   <AgButton variant="primary">Save</AgButton>
 * </AgModalActions>
 *
 * // Fully custom cancel element
 * <AgModalActions cancel={<AgButton variant="destructive">Discard</AgButton>}>
 *   <AgButton variant="primary">Save</AgButton>
 * </AgModalActions>
 *
 * // No cancel button
 * <AgModalActions cancel={null}>
 *   <AgButton variant="primary" onPress={onClose}>OK</AgButton>
 * </AgModalActions>
 * ```
 */
export function AgModalActions({ children, onCancel, cancel, className }: AgModalActionsProps) {
  const ctx = useContext(AgModalContext);
  const handleCancel = onCancel ?? ctx?.onClose;

  const cancelNode =
    cancel !== undefined ? (
      cancel
    ) : handleCancel ? (
      <AgButton variant="white" onPress={handleCancel}>
        Cancel
      </AgButton>
    ) : null;

  return (
    <div className={cn('flex items-center justify-between gap-2 mt-5', className)}>
      <div>{cancelNode}</div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
AgModalActions.displayName = 'AgModalActions';
